import {
  settingsSectionFromSearch, type SettingsSectionId,
} from './settingsSections'

/**
 * The pages of the sidebar Settings layout (`settings-sidebar`), in order.
 * `keywords` are what the settings search matches besides the label: the
 * words someone looking for a thing on that page would type.
 */
export const SETTINGS_PAGES = [
  {
    id: 'overview', group: 'Overview', label: 'Overview',
    description: 'Everything set up for your assistant, at a glance.',
    keywords: 'summary status home',
  },
  {
    id: 'mail', group: 'Connections', label: 'Mail and calendar',
    description: 'The inboxes and calendar your assistant works from.',
    keywords: 'email inbox gmail outlook icloud imap calendar additional inbox personal',
  },
  {
    id: 'chat', group: 'Connections', label: 'Where you chat',
    description: 'The places you can message your assistant.',
    keywords: 'messaging whatsapp imessage slack sms text teams assistant email address',
  },
  {
    id: 'apps', group: 'Connections', label: 'Apps',
    description: 'Tools your assistant can read and update for you.',
    keywords: 'integrations connect zoom meet granola fathom otter notion drive docs asana clickup hubspot salesforce attio crm webhook custom',
  },
  {
    id: 'assistant', group: 'Assistant', label: 'Name and personality',
    description: 'Who your assistant is.',
    keywords: 'name rename character picture photo personality bio',
  },
  {
    id: 'permissions', group: 'Assistant', label: 'What it can do alone',
    description: 'What your assistant does without asking you first.',
    keywords: 'automatic approvals permissions auto approve texting contacts consent',
  },
  {
    id: 'phone', group: 'Assistant', label: 'Phone and iPhone',
    description: 'Calls, a number of its own, and voice notes from your iPhone.',
    keywords: 'dedicated phone number call voice iphone action button shortcut location',
  },
  {
    id: 'billing', group: 'Account', label: 'Plan and billing',
    description: 'Your subscription and what you buy on top of it.',
    keywords: 'subscription plan payment invoice cancel credits balance top up add-ons',
  },
] as const

export type SettingsPageId = (typeof SETTINGS_PAGES)[number]['id']

/**
 * The page each linkable section lives on. A `Record` over every section, so a
 * new section is a type error here until it is given a page; otherwise a link
 * to it would open a page that does not hold it.
 */
const SECTION_PAGE: Record<SettingsSectionId, SettingsPageId> = {
  'mail-calendar': 'mail',
  'additional-inboxes': 'mail',
  'messaging': 'chat',
  'assistant-email': 'chat',
  'automatic-approvals': 'permissions',
  'texting-contacts': 'permissions',
  'dedicated-number': 'phone',
  'iphone-action-button': 'phone',
  'purchase-credits': 'billing',
}

export function isSettingsPage(raw: string): raw is SettingsPageId {
  return SETTINGS_PAGES.some((page) => page.id === raw)
}

export function pageForSection(section: SettingsSectionId): SettingsPageId {
  return SECTION_PAGE[section]
}

/** The page a Settings URL names through `?section=`, if any. */
export function pageFromSearch(search: string): SettingsPageId | null {
  const section = settingsSectionFromSearch(new URLSearchParams(search))
  return section ? pageForSection(section) : null
}

/** The pages a settings search matches, by label or keyword, in sidebar order. */
export function matchSettingsPages(query: string): readonly (typeof SETTINGS_PAGES)[number][] {
  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean)
  if (words.length === 0) return SETTINGS_PAGES
  return SETTINGS_PAGES.filter((page) => {
    const haystack = `${page.label} ${page.keywords}`.toLowerCase()
    return words.every((word) => haystack.includes(word))
  })
}
